// The whole app: sign-in gate, conversation rail, transcript, composer.
//
// WHAT THE SERVER OWNS AND WHAT THIS FILE OWNS. The server decides what a turn
// produced, which documents it cited, and what the follow-up chips say. This file
// decides only how that is arranged on screen and what happens when something is
// clicked. Keeping the split there is what stops the citation a user sees from
// disagreeing with the one that was recorded.
//
// The localStorage cache is a CACHE, never the record. The server is authoritative
// for every conversation; this only makes a reload feel instant and keeps the last
// exchange visible if the network is down.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bubble } from "./components/Bubble";
import { CardView } from "./components/CardView";
import { Chips } from "./components/Chips";
import { Composer } from "./components/Composer";
import { Feedback, type Rating } from "./components/Feedback";
import { Typing } from "./components/Typing";
import { EmailModal } from "./components/EmailModal";
import {
  acceptAddition,
  deleteConversation,
  HistoryUnavailable,
  listConversations,
  loadConversation,
  NotAuthenticatedYet,
  sendChat,
} from "./api/client";
import { authRequired, clientName, NotAuthenticatedError, signOut } from "./auth/msal";
import { applyChoice, readChoice, resolveMode, watchSystemTheme, type ThemeMode } from "./theme";
import { draftToHtmlBody } from "./lib/draftHtml";
import type { Card, CardAction, ChatItem, ChatTurn, ConversationSummary, DraftState, EmailContext } from "./types";

/** A floor on the waiting state, and only when nothing has streamed yet.
 *
 *  A reply that lands in 90ms flashes the typing indicator for one frame, which
 *  reads as a glitch rather than as speed. The floor never delays text the user can
 *  already see — that would be the opposite of what it is for. */
const MIN_THINK_MS = 500;

const STORAGE_KEY = "crown-hr-chat-v1";

/** How much of the transcript the cache keeps. Bounded because localStorage has a
 *  hard quota and a long conversation of cards is not small; the server holds the
 *  rest. */
const CACHE_ITEMS = 60;
const CACHE_HISTORY = 16;

/** How many prior turns go to the model. The server caps this again — this is the
 *  transport half, and both exist so neither side has to trust the other. */
const SENT_HISTORY = 8;

let uid = 0;
const nextId = () => ++uid;

// Seeded below, once `readCache()` exists — see the call at the bottom of this
// section. Reported live 2026-10-01: scrolling up revealed an old question's
// content silently replaced by a newer one. `uid` resets to 0 on every page
// reload, but the restored cache below still holds items numbered from a PRIOR
// session's counter, starting from 1 too. The very next nextId() call after a
// reload collides with an already-rendered cached id, React logs "Encountered two
// children with the same key", and one of the two items sharing that key loses its
// own content to the other's — which a user scrolling back through history would
// see as an old message getting overwritten, with no send/receive required to
// trigger it beyond whatever first message happens to come after the reload.

interface Persisted {
  items?: ChatItem[];
  history?: ChatTurn[];
  conversationId?: string | null;
}

function readCache(): Persisted | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Persisted) : null;
  } catch {
    // Storage is unavailable in some privacy modes, and throws rather than
    // returning null. A cache is an optimisation; never fail a load over one.
    return null;
  }
}

// Runs once, at module load — before any component has had the chance to call
// nextId() for a brand-new item. Every id this session ever mints is now
// guaranteed to be higher than any id already sitting in the restored cache, so a
// new item can never collide with — and silently overwrite — an old one.
for (const item of readCache()?.items ?? []) {
  if (typeof item.id === "number" && item.id > uid) uid = item.id;
}

/** Strip tags for the copy sent BACK to the model as history.
 *
 *  To a fixed point, because a single pass can reassemble a tag: "<<b>b>" becomes
 *  "<b>" after one removal. Cheap here, and the same rule the server applies. */
function stripHtml(html: string): string {
  let prev = html;
  for (;;) {
    const next = prev.replace(/<[^>]*>/g, "");
    if (next === prev) return next.trim();
    prev = next;
  }
}

function initials(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

// THE SIGN-IN GATE MOVED TO AuthGate.tsx. It lived here while this was the only page;
// /dashboard then called authHeaders() with auth never initialised and every panel 401'd.
// The gate now wraps whatever main.tsx renders, so a page cannot be added outside it.

export default function Chat({ account }: { account: { name?: string; username?: string } | null }) {
  const cached = useMemo(readCache, []);

  const [items, setItems] = useState<ChatItem[]>(cached?.items ?? []);
  const [chips, setChips] = useState<string[]>([]);
  const [thinking, setThinking] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(cached?.conversationId ?? null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [historyAvailable, setHistoryAvailable] = useState(true);
  const [railOpen, setRailOpen] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [email, setEmail] = useState<EmailContext | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [mode, setMode] = useState<ThemeMode>(() => resolveMode());

  const historyRef = useRef<ChatTurn[]>(cached?.history ?? []);
  const draftRef = useRef<DraftState | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  /** Sign out, and take the cached transcript with it.
   *
   *  ⚠ THE CACHE CLEAR IS THE SECURITY HALF, and it was missing until 2026-09-14.
   *  STORAGE_KEY lives in localStorage and is NOT scoped by user, so on a shared HR
   *  machine the sequence was: one person signs out, the next signs in, this component
   *  mounts, readCache() returns the previous person's conversation, and their HR
   *  questions and answers render for someone else. No server call is involved, so
   *  none of the ownership scoping in the store could have caught it.
   *
   *  Exactly the reasoning already applied to TOKENS in auth/msal.ts ("a token in
   *  localStorage outlives the tab and is shared across every tab on the origin. On a
   *  shared HR machine that is a signed-in session the next person inherits"). The
   *  transcript needed the same treatment and did not get it.
   *
   *  Cleared HERE rather than inside signOut() because this component owns the key;
   *  reaching into it from auth/ would make the two files import each other. */
  function handleSignOut() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Unavailable in some privacy modes. Nothing was cached in that case either.
    }
    signOut();
  }

  // --- theme ---------------------------------------------------------------
  useEffect(() => watchSystemTheme(setMode), []);
  const cycleTheme = () => {
    // Two visible states, cycling light -> dark -> follow the system. "System" is
    // last rather than absent: a user who tries the toggle should be able to get
    // back to tracking their OS without clearing site data.
    const order = ["light", "dark", "system"] as const;
    const next = order[(order.indexOf(readChoice()) + 1) % order.length];
    setMode(applyChoice(next));
    setToast(next === "system" ? "Following your system theme" : `${next[0].toUpperCase()}${next.slice(1)} theme`);
  };

  // --- toast ---------------------------------------------------------------
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // --- scroll --------------------------------------------------------------
  //
  // Follow the stream, but ONLY while the reader is already at the bottom.
  //
  // This used to assign scrollTop unconditionally on every streamed chunk, which meant
  // scrolling up to re-read an earlier answer was impossible during a reply: the next
  // token dragged the view straight back down. Worse, it did that against
  // `scroll-behavior: smooth` (now removed from .log), so each yank animated over the
  // last one and the view lagged behind the text.
  //
  // NEAR_BOTTOM_PX is a tolerance, not a guess at intent. Exact equality never holds:
  // fractional device-pixel ratios, the smooth-scroll settle, and a chunk that arrives
  // mid-scroll all leave a few pixels of slop, and a strict test would read as "the
  // user scrolled away" on every other frame and stop following at all.
  const atBottomRef = useRef(true);
  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    const NEAR_BOTTOM_PX = 64;
    const onScroll = () => {
      atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = logRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [items, thinking]);

  // A new turn is the user's own action, so it always returns them to the bottom even
  // if they had scrolled away to read. Without this, sending a message from halfway up
  // the history leaves the answer streaming somewhere off-screen below.
  useEffect(() => {
    if (thinking) atBottomRef.current = true;
  }, [thinking]);

  // --- cache ---------------------------------------------------------------
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          items: items.slice(-CACHE_ITEMS),
          history: historyRef.current.slice(-CACHE_HISTORY),
          conversationId,
        } satisfies Persisted),
      );
    } catch {
      /* quota or private mode — the server still has everything */
    }
  }, [items, conversationId]);

  // --- conversation list ---------------------------------------------------
  const refreshList = useCallback(async () => {
    try {
      setConversations(await listConversations());
      setHistoryAvailable(true);
    } catch (err) {
      // A backend with no database is a supported state, and the rail must then
      // disappear rather than render an empty list — "you have no chats" and "chat
      // history is switched off" are different things to tell someone.
      if (err instanceof HistoryUnavailable) setHistoryAvailable(false);
      else if (!(err instanceof NotAuthenticatedYet || err instanceof NotAuthenticatedError)) {
        console.warn("[conversations] list failed:", err);
      }
    }
  }, []);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  // --- transcript helpers --------------------------------------------------
  const pushItem = (item: ChatItem) => setItems((prev) => [...prev, item]);
  const replaceItem = (id: number, item: ChatItem) => setItems((prev) => prev.map((x) => (x.id === id ? item : x)));
  const removeItem = (id: number) => setItems((prev) => prev.filter((x) => x.id !== id));

  function startNewChat() {
    historyRef.current = [];
    draftRef.current = null;
    setItems([]);
    setChips([]);
    setConversationId(null);
    setEmail(null);
    setRailOpen(false);
    inputRef.current?.focus();
  }

  async function openConversation(id: string) {
    setRailOpen(false);
    try {
      const { messages } = await loadConversation(id);
      const restored: ChatItem[] = messages
        .map((m) => m.body)
        .filter((b): b is ChatItem => !!b)
        .map((b) => ({ ...b, id: nextId() }));
      setItems(
        restored.length
          ? restored
          : [{ id: nextId(), kind: "bubble", role: "system", html: "This chat has no messages left to show." }],
      );
      // Rebuild what the MODEL sees, not just what the reader sees. Without this a
      // resumed conversation is coherent on screen and amnesiac to the assistant,
      // so the first follow-up gets answered as if it opened the conversation.
      historyRef.current = restored
        .filter((i): i is Extract<ChatItem, { kind: "bubble" }> => i.kind === "bubble" && i.role !== "system")
        .map((i) => ({ role: i.role === "user" ? "user" : "assistant", text: stripHtml(i.html) }));
      // The draft is NOT restored: a card's buttons read their own draftPreview, so
      // an old draft card still works, and carrying a stale draft into a new turn
      // would silently revise something the user thinks they left behind.
      draftRef.current = null;
      setConversationId(id);
      setChips([]);
      setEmail(null);
    } catch (err) {
      console.warn("[conversations] open failed:", err);
      setToast("That chat could not be opened.");
    }
  }

  async function removeConversation(id: string) {
    // Optimistic: the row disappears immediately. A failed delete is put back by the
    // refresh below rather than blocking on a spinner.
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (id === conversationId) startNewChat();
    try {
      await deleteConversation(id);
    } catch {
      setToast("That chat could not be deleted.");
    }
    void refreshList();
  }

  // --- the turn ------------------------------------------------------------
  /** `fromSuggestion` marks a turn the USER TAPPED rather than typed — a follow-up
   *  chip, a welcome prompt, or Draft-a-reply. The backend needs it because those
   *  strings are ours, written off the previous answer, so they continue that topic by
   *  construction; its `needsContext` heuristic has only the words and read "Draft an
   *  email to HR leadership" as a fresh question, losing the policy from the search.
   *  Anything the user types stays false and is judged on its own words. */
  async function runTurn(message: string, echo = true, fromSuggestion = false) {
    if (echo) pushItem({ id: nextId(), kind: "bubble", role: "user", html: message });
    historyRef.current.push({ role: "user", text: message });
    setChips([]);
    setThinking(true);
    const startedAt = Date.now();

    try {
      // The lead-in arrives seconds before the card. Showing it as soon as it lands
      // is the difference between a ten-second blank spinner and an immediate reply.
      // `streamedId` remembers that bubble so the authoritative text from the `done`
      // event REPLACES it rather than appearing underneath — the rendered result
      // must not depend on which transport was used.
      let streamedId: number | null = null;
      const res = await sendChat(
        {
          message,
          email,
          history: historyRef.current.slice(-SENT_HISTORY),
          draft: draftRef.current,
          conversationId,
          fromSuggestion,
        },
        (text) => {
          if (!text.trim() || streamedId !== null) return;
          streamedId = nextId();
          // THE INDICATOR STAYS UP. This used to call setThinking(false) here, reasoning
          // that the reply was on screen so the indicator had become a lie. It was the
          // opposite: the lead-in is one or two sentences that land seconds before the
          // card, and clearing the indicator left the user looking at a finished-looking
          // message with nothing happening for the several seconds the card takes. A
          // stalled app and a working one were indistinguishable, and the lead-in makes
          // it worse by looking like a complete answer.
          //
          // `thinking` means "more of this turn is still coming", which is true until
          // the `done` event. The finally block clears it on every path, including
          // errors, so this cannot strand the indicator.
          pushItem({ id: streamedId, kind: "bubble", role: "agent", html: text });
        },
      );

      const elapsed = Date.now() - startedAt;
      if (streamedId === null && elapsed < MIN_THINK_MS) {
        await new Promise((r) => setTimeout(r, MIN_THINK_MS - elapsed));
      }

      if (streamedId !== null) {
        // The server's first bubble is authoritative. A turn that ends with no
        // bubbles at all (a refusal rewrites them) drops the streamed one entirely.
        const id = streamedId as number;
        if (res.bubbles[0]) replaceItem(id, { id, kind: "bubble", role: "agent", html: res.bubbles[0] });
        else removeItem(id);
        for (const b of res.bubbles.slice(1)) pushItem({ id: nextId(), kind: "bubble", role: "agent", html: b });
      } else {
        for (const b of res.bubbles) pushItem({ id: nextId(), kind: "bubble", role: "agent", html: b });
      }

      if (res.bubbles[0]) historyRef.current.push({ role: "assistant", text: stripHtml(res.bubbles[0]) });
      if (res.card) pushItem({ id: nextId(), kind: "card", card: res.card });
      if (!res.bubbles.length && !res.card) {
        pushItem({
          id: nextId(),
          kind: "bubble",
          role: "agent",
          html: "I didn't catch that. Could you rephrase, or tap a suggestion below?",
        });
      }

      // Offer a rating only where "was this right?" is a meaningful question. A
      // thumbs control under "Hi, how can I help?" is noise, and noise is what
      // teaches people to ignore the control — which would cost the one direct
      // quality signal this product has. Null id means no database, so the control
      // would do nothing at all.
      if (res.usageEventId && (res.card || res.bubbles.length)) {
        pushItem({ id: nextId(), kind: "feedback", usageEventId: res.usageEventId, rating: null });
      }

      if (res.chips) setChips(res.chips);
      if (res.draft !== undefined) draftRef.current = res.draft ?? null;
      if (res.conversationId && res.conversationId !== conversationId) {
        setConversationId(res.conversationId);
        setHistoryAvailable(true);
        void refreshList();
      }
    } catch (err) {
      // THREE DIFFERENT FAILURES, three different things to do about them. Folding
      // them into one "something went wrong" is what leaves a user in the wrong
      // tenant retrying forever with no hint that an admin could fix it.
      const html =
        err instanceof NotAuthenticatedError
          ? "Your session has expired. <b>Reload the page</b> to sign in again."
          : err instanceof NotAuthenticatedYet
            ? "This account can't use the assistant yet. It may be outside your organisation, or an administrator may still need to approve access."
            : "Something went wrong reaching the assistant. Please try again.";
      pushItem({ id: nextId(), kind: "bubble", role: "system", html });
    } finally {
      setThinking(false);
    }
  }

  // --- card actions --------------------------------------------------------
  async function onAction(action: CardAction, card: Card) {
    // THE CARD'S OWN draftPreview, never a shared "latest draft" ref. A ref holds
    // one draft and is cleared when a conversation is restored, which leaves older
    // cards on screen with live buttons that would copy nothing at all.
    const text = card.draftPreview ?? draftRef.current?.text ?? "";

    if (action.id === "copyDraft") {
      if (!text) return setToast("There's no draft text to copy.");
      const ok = await copyDraft(text);
      // The clipboard API needs a secure context and a user gesture, and still
      // refuses in some embedded browsers. Say what happened rather than silently
      // doing nothing — the draft is on screen and can be selected by hand.
      setToast(ok ? "Draft copied." : "Couldn't copy automatically — select the draft text and copy it.");
      return;
    }

    if (action.id === "draftReply") {
      void runTurn("Draft a reply to this email.", true, true);
      return;
    }

    if (action.id === "acceptAddition") {
      const text = String((action.data ?? {}).text ?? "");
      if (!text) return setToast("There's nothing to add.");
      if (!conversationId) {
        // Only reachable with no database: the conversation was never created, so
        // there is nowhere to put this. Said plainly rather than failing quietly,
        // because the user is watching for a confirmation.
        return setToast("Can't save that right now — history is unavailable.");
      }
      const saved = await acceptAddition(conversationId, text);
      if (!saved) return setToast("Couldn't save that. Try again.");

      // RE-ASK THE ORIGINAL QUESTION, without echoing it as a new user bubble. The
      // server answered the last turn as if the fact did not exist, which is what the
      // user is now overriding, so the answer has to be rebuilt rather than patched.
      //
      // The question is read back off historyRef rather than stored separately: the
      // confirm turn pushed the SAME message that produced it, so the last user entry
      // is the question, and one source of truth cannot drift from the other.
      const lastQuestion = [...historyRef.current].reverse().find((t) => t.role === "user")?.text;
      if (!lastQuestion) return setToast("Added. Ask again to use it.");
      setToast("Added for this conversation.");
      void runTurn(lastQuestion, false, true);
      return;
    }

    if (action.id === "declineAddition") {
      // Nothing was stored, so there is nothing to tell the server. Dropping the card
      // leaves the answer that was already given, which was built without the fact.
      //
      // Matched on the card OBJECT rather than by id, and updated functionally: `items`
      // in this closure is the render that drew the button, and a turn may have landed
      // since. Identity is exact here because the card came from that same array.
      setItems((prev) => prev.filter((x) => !(x.kind === "card" && x.card === card)));
      setToast("Not added.");
      return;
    }
  }

  const showWelcome = items.length === 0 && !thinking;

  return (
    <>
      <a className="skip-link" href="#composer-input">
        Skip to the message box
      </a>

      <div className="app">
        {railOpen && <button className="scrim-rail" aria-label="Close the chat list" onClick={() => setRailOpen(false)} />}

        <nav className="rail" data-open={railOpen} aria-label="Your chats">
          <div className="rail-head">
            <div className="rail-mark" aria-hidden="true">
              C
            </div>
            <h1 className="wordmark">
              {clientName()}
              <span>HR Assistant</span>
            </h1>
          </div>

          <button type="button" className="new-chat" onClick={startNewChat}>
            <span aria-hidden="true">＋</span> New chat
          </button>

          {historyAvailable && (
            <>
              <div className="rail-label">Recent</div>
              <div className="rail-list">
                {conversations.length === 0 ? (
                  <p className="rail-empty">Your chats will appear here.</p>
                ) : (
                  conversations.map((c) => (
                    <div key={c.id} className="rail-item" aria-current={c.id === conversationId}>
                      <button
                        type="button"
                        className="rail-item-title"
                        style={{ border: 0, background: "transparent", padding: 0, textAlign: "left" }}
                        onClick={() => void openConversation(c.id)}
                      >
                        {c.title || "Untitled chat"}
                      </button>
                      <button
                        type="button"
                        className="rail-item-del"
                        aria-label={`Delete ${c.title || "this chat"}`}
                        onClick={() => void removeConversation(c.id)}
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          <div className="rail-foot">
            <div className="avatar" aria-hidden="true">
              {initials(account?.name || account?.username)}
            </div>
            <div className="rail-user">
              <b>{account?.name || account?.username || "Signed in"}</b>
              {account?.name && account.username && <span>{account.username}</span>}
            </div>
            {/* A plain link, not a click handler: /dashboard is a separate full page and
                the server's catch-all already serves the SPA for it. Navigating away
                loses nothing — the transcript is cached and the server holds the
                conversation. */}
            <a className="icon-btn" href="/dashboard" title="Usage dashboard" aria-label="Usage dashboard">
              ▦
            </a>
            <button type="button" className="icon-btn" onClick={cycleTheme} title="Change theme" aria-label="Change theme">
              {mode === "dark" ? "☾" : "☀"}
            </button>
            {authRequired() && (
              <button type="button" className="icon-btn" onClick={handleSignOut} title="Sign out" aria-label="Sign out">
                ⏻
              </button>
            )}
          </div>
        </nav>

        <div className="main">
          <div className="topbar">
            <button type="button" className="icon-btn" onClick={() => setRailOpen(true)} aria-label="Open the chat list">
              ☰
            </button>
            <div className="topbar-title">{clientName()} HR Assistant</div>
          </div>

          <div className="log" ref={logRef}>
            <div className="log-inner">
              {showWelcome && <Welcome onPick={(t) => void runTurn(t, true, true)} onAttach={() => setEmailModal(true)} />}

              {items.map((item) => {
                if (item.kind === "bubble") return <Bubble key={item.id} role={item.role} html={item.html} />;
                if (item.kind === "card") {
                  return (
                    <CardView
                      key={item.id}
                      card={item.card}
                      onAction={(a, c) => void onAction(a, c)}
                      onOpen={(url) => window.open(url, "_blank", "noopener,noreferrer")}
                    />
                  );
                }
                return (
                  <Feedback
                    key={item.id}
                    usageEventId={item.usageEventId}
                    value={item.rating ?? null}
                    onChange={(rating: Rating) => replaceItem(item.id, { ...item, rating })}
                  />
                );
              })}

              {thinking && <Typing />}
              {!thinking && chips.length > 0 && <Chips chips={chips} onChip={(t) => void runTurn(t, true, true)} />}
            </div>
          </div>

          <Composer
            ref={inputRef}
            onSend={(t) => void runTurn(t)}
            disabled={thinking}
            onAttachEmail={() => setEmailModal(true)}
            attachedSubject={email ? email.subject : null}
            onClearEmail={() => setEmail(null)}
          />
        </div>
      </div>

      {emailModal && (
        <EmailModal
          onCancel={() => setEmailModal(false)}
          onSubmit={(next) => {
            setEmail(next);
            setEmailModal(false);
            void runTurn("Draft a reply to this email.", true, true);
          }}
        />
      )}

      {toast && (
        <div className="toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </>
  );
}

/** The welcome screen's starter questions.
 *
 *  ⚠ EVERY STARTER HERE MUST BE ANSWERABLE FROM CROWN'S ACTUAL INDEX, and each one below
 *  was verified against it before shipping. This is not a style rule.
 *
 *  A suggestion is a PROMISE that an answer exists. The first version of this screen
 *  offered "What's our policy on jury duty leave?", written from intuition rather than
 *  from the corpus — and Crown's index has no jury duty policy, because the synced
 *  library is a BENEFITS knowledge base. So the app's own front door demonstrated the
 *  decline path to every new user on their first click, which reads as a broken product
 *  rather than as the honest refusal it actually is.
 *
 *  Verified 2026-09-11 by `backend/scripts/prod-eval.mjs` against prod. All three
 *  returned `declined:false` with citations:
 *    PTO carryover        citedSources 1, 8 passages   (accrual bands and carryover cap)
 *    Health enrollment    citedSources 2, 8 passages   (eligibility, Appendix B)
 *    FMLA request         citedSources 2, 6 passages   (notify supervisor + HR Manager)
 *
 *  ⚠ THE WORDING WAS CHANGED AFTER THAT RUN. These are phrased from the HR TEAM's point
 *  of view ("how does an employee enroll"), not the employee's ("how do I enroll"),
 *  because the people typing here answer questions rather than ask them. The topics are
 *  the verified ones, but the exact strings sent to retrieval are not the exact strings
 *  that were measured, and retrieval is sensitive to phrasing. Re-run them.
 *
 *  Before changing any of these, run them through prod-eval.mjs and check the answer
 *  carries a source row. `backend/scripts/corpus-inventory.mjs` lists what the index
 *  actually holds, which is where a new suggestion should come from. */
function Welcome({ onPick, onAttach }: { onPick: (text: string) => void; onAttach: () => void }) {
  return (
    <div className="welcome">
      <h1>What can I look up?</h1>
      <p>
        Ask about a policy and I'll answer from {clientName()}'s own documents, with a link to each one. Ask me to draft
        something and I'll write it from those same documents, for you to edit before anyone sees it.
      </p>
      <div className="starters">
        <button type="button" className="starter" onClick={() => onPick("How does PTO carryover work for employees?")}>
          <b>How does PTO carryover work for employees?</b>
          <span>Accrual bands and what rolls into next year</span>
        </button>
        <button type="button" className="starter" onClick={() => onPick("How does an employee enroll in health benefits?")}>
          <b>How does an employee enroll in health benefits?</b>
          <span>Eligibility, and the steps they take</span>
        </button>
        <button
          type="button"
          className="starter"
          onClick={() => onPick("Draft a note to an employee explaining how to request FMLA leave.")}
        >
          <b>Draft a note about requesting FMLA leave</b>
          <span>A reply written from the policy, ready to edit</span>
        </button>
        <button type="button" className="starter" onClick={onAttach}>
          <b>Reply to an employee's email</b>
          <span>Paste it in and iterate on the draft</span>
        </button>
      </div>
    </div>
  );
}

/** Put the draft on the clipboard, rich and plain at once.
 *
 *  BOTH FLAVOURS, in one write. A draft may contain markdown links, and only to
 *  URLs the model was actually shown (normalizeDraftText vets them server-side).
 *  Copying plain text alone pastes those literally, so an employee receives
 *  "[HR-118 Paid Time Off](https://…)" in the body of an HR reply — which reads as
 *  a bug in the email. Offering text/html as well lets a mail client take the real
 *  anchors while anything plain still gets clean text.
 *
 *  Falls back to a plain write when ClipboardItem is missing (older Safari, some
 *  embedded browsers) rather than failing: a plain copy is worse than a rich one and
 *  far better than none. Returns false only when nothing reached the clipboard. */
async function copyDraft(text: string): Promise<boolean> {
  try {
    if (typeof ClipboardItem === "function" && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([draftToHtmlBody(text)], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      return true;
    }
  } catch {
    /* fall through to the plain write */
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
